import { useId } from 'react';

import { Alert } from '@/components/ui/Alert';
import { AmountSlabs } from '@/components/ui/AmountSlabs';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { SelectField, TextField } from '@/components/ui/Field';
import { FormGrid, FormGridWide } from '@/components/ui/FormGrid';
import { useToast } from '@/components/ui/Toast';
import { useForm } from '@/hooks/useForm';
import { todayDateOnly } from '@/utils/date';
import { formatMoney, toPaise } from '@/utils/money';

import { useRecordPayment } from '../../hooks/useInvoices';
import {
  DEFAULT_PAYMENT_METHOD,
  type Invoice,
  PAYMENT_METHOD_LABEL,
  PAYMENT_METHODS,
  type PaymentKind,
  recordPaymentFormSchema,
} from '../../schemas/invoice.schema';

const METHOD_OPTIONS = PAYMENT_METHODS.map((method) => ({
  value: method,
  label: PAYMENT_METHOD_LABEL[method],
}));

export interface RecordPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice;
  /** A cancelled bill only ever takes money back out. */
  kind: PaymentKind;
}

export function RecordPaymentDialog({ open, onClose, invoice, kind }: RecordPaymentDialogProps) {
  const formId = useId();
  const record = useRecordPayment(invoice.id);
  const isRefund = kind === 'refund';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={isRefund ? 'Record a refund' : 'Record a payment'}
      description={
        isRefund
          ? `${formatMoney(invoice.amountPaid)} has been paid on bill ${invoice.invoiceNumber}. A refund can be at most that much.`
          : `${formatMoney(invoice.dueAmount)} is still due on bill ${invoice.invoiceNumber}.`
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={formId} isLoading={record.isPending}>
            {isRefund ? 'Record refund' : 'Record payment'}
          </Button>
        </>
      }
    >
      <PaymentForm formId={formId} invoice={invoice} kind={kind} record={record} onDone={onClose} />
    </Dialog>
  );
}

function PaymentForm({
  formId,
  invoice,
  kind,
  record,
  onDone,
}: {
  formId: string;
  invoice: Invoice;
  kind: PaymentKind;
  record: ReturnType<typeof useRecordPayment>;
  onDone: () => void;
}) {
  const toast = useToast();
  // Defaults to the whole amount outstanding (or, for a refund, everything taken so far) —
  // the usual case at the counter, and still editable.
  const form = useForm({
    kind,
    amount: kind === 'refund' ? invoice.amountPaid : invoice.dueAmount,
    method: DEFAULT_PAYMENT_METHOD,
    paidOn: todayDateOnly(),
    reference: '',
  });

  return (
    <form
      id={formId}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const input = form.parse(recordPaymentFormSchema);
        if (!input) return;
        record.mutate(input, {
          onSuccess: (updated) => {
            toast.success(
              kind === 'refund'
                ? `Refund recorded — ${formatMoney(updated.amountPaid)} now stands paid`
                : updated.paymentState === 'paid'
                  ? `Payment recorded — bill ${updated.invoiceNumber} is paid in full`
                  : `Payment recorded — ${formatMoney(updated.dueAmount)} still due`,
            );
            onDone();
          },
          onError: form.handleError,
        });
      }}
    >
      <FormGrid>
        <TextField
          label="Amount"
          prefix="₹"
          inputMode="decimal"
          required
          autoFocus
          {...form.field('amount')}
        />
        {/* Instalments are a share of what is still due. A refund is an exact figure, so no
            quick-picks there — a percentage button would only invite a wrong amount. */}
        {kind === 'payment' && (
          <FormGridWide>
            <AmountSlabs
              basePaise={toPaise(invoice.dueAmount)}
              value={form.values.amount}
              onPick={(amount) => {
                form.setValue('amount', amount);
              }}
            />
          </FormGridWide>
        )}
        <SelectField label="Method" options={METHOD_OPTIONS} required {...form.field('method')} />
        <TextField label="Date" type="date" required {...form.field('paidOn')} />
        <TextField
          label="Reference"
          hint="Optional — UPI reference, cheque number…"
          {...form.field('reference')}
        />
        {form.formError && (
          <FormGridWide>
            <Alert tone="danger">{form.formError}</Alert>
          </FormGridWide>
        )}
      </FormGrid>
    </form>
  );
}
